/** REVIEW_GUEST_SETUP control: create the app-store review guest and link to its
 *  profile IN THE CONSOLE (/users/<id>) once it exists.
 *
 *  When the guest isn't set up yet, ops can create it (User + Tour_Operator_User) via
 *  the execute endpoint; its MyTrips of two audio activities stays a dashboard step.
 */
import { Box, Button, Link, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import type { PreflightItem } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { userActions } from '../tenantLinks';

export function ReviewGuestControl({
  item,
  topId,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const guestUserId = typeof item.detail.guest_user_id === 'number' ? item.detail.guest_user_id : null;
  const realEmail = typeof item.detail.guest_email === 'string' ? item.detail.guest_email : null;
  const email = realEmail ?? 'the review guest';
  const exists = item.detail.guest_exists === true || guestUserId !== null;

  const create = useMutation({
    mutationFn: () => items.execute(topId, item.id),
    onSuccess: (res) => {
      const failed = !res.run || res.run.status === 'error';
      snackbar.show(
        failed ? res.run?.error_message || 'Could not create the review guest.' : 'Review guest created.',
        failed ? 'error' : 'success',
      );
      onChanged();
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Could not create the review guest.', 'error'),
  });

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={1}>
        {guestUserId !== null ? (
          <Typography variant="body2">
            <Link component={RouterLink} to={`/users/${guestUserId}`}>
              Open the guest profile
            </Link>{' '}
            ·{' '}
            {/* `email` falls back to prose when the payload has none — only
                pass a real address, or the mailto would be nonsense. */}
            <ValueActions actions={userActions({ id: guestUserId, email: realEmail })}>
              {email}
            </ValueActions>
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Review guest ({email}) not set up yet.
          </Typography>
        )}
        {canManage && !exists && (
          <Box>
            <Button size="small" variant="contained" disabled={create.isPending} onClick={() => create.mutate()}>
              Create review guest
            </Button>
          </Box>
        )}
        {exists && (
          <Typography variant="caption" color="text.secondary">
            Add its MyTrips (two audio activities) in the dashboard for app-store review.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
